import { pickKeys } from "@captions-web/utils/pick-by";

// we allow passing options when creating the hook and when calling the hook.
// this merges them in a "smart" way to handle callbacks correctly by calling
// all callbacks when the same callback is passed in both places instead of
// overwriting them. bad types are fine here it'd be super hard to get right
// otherwise.
export function mergeCallbackOptions<ObjectType extends object>(
  callbacks: Array<keyof ObjectType>,
  ...optionsList: ObjectType[]
) {
  const callbacksSet = new Set(callbacks);

  return optionsList.reduce((acc, options) => {
    // Iterate over each key in the current options
    for (const key in options) {
      const optionKey = key as keyof ObjectType;

      if (callbacksSet.has(optionKey)) {
        // If the key is a callback, merge the functions
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const existingCallback = acc[optionKey] as any;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const newCallback = options[optionKey] as any;

        acc[optionKey] = (async (...args: unknown[]) => {
          await existingCallback?.(...args);
          await newCallback?.(...args);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }) as any;
      } else {
        // Otherwise, just assign the value to the accumulator
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        acc[optionKey] = options[optionKey] as any;
      }
    }

    return acc;
  }, {} as ObjectType);
}

export function mergeCallbackOptionsWithPick<
  ObjectType extends object,
  Keys extends keyof ObjectType,
  RestObjects extends Pick<ObjectType, Keys>,
>(callbacks: Array<Keys>, mainObject: ObjectType, ...optionsList: RestObjects[]) {
  return mergeCallbackOptions(
    callbacks,
    ...optionsList.concat(mainObject as unknown as RestObjects).map((o) => pickKeys(o, callbacks))
  );
}
